import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Card,
  Col,
  Form,
  InputGroup,
  Row,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BsSend } from 'react-icons/bs';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';

import api from 'api';
import useRequest from 'hooks/useRequest';
import { MAX_PAGE_SIZE } from 'utils/constants';
import { BurncenterAccessLevelEnum, Roles, RolesLabels } from 'utils/enums';
import { BurnCenterType } from 'types/burnCenter';
import SVButton from 'components/elements/SVButton';

import QuillRichTextInput from './QuillRichTextInput';
import {
  AudienceType,
  CreateMessagePayload,
  PostedMessage,
} from './types';

type FormValues = {
  title: string;
  body: string;
};

type Props = {
  onSubmit: (message: CreateMessagePayload) => Promise<void>;
  isSubmitting?: boolean;
  editingMessage?: PostedMessage | null;
  onCancelEdit?: () => void;
};

const TARGET_ROLES = [
  Roles.bdataAdmin,
  Roles.abaAdmin,
  Roles.manager,
  Roles.burnCenterUser,
] as const;

const ACCESS_LEVELS = Object.values(BurncenterAccessLevelEnum);

const formatBurnCenterLabel = (center: BurnCenterType) => (
  center.hospitalName ? `${center.name} - ${center.hospitalName}` : center.name
);

const PostMessageForm: React.FC<Props> = ({
  onSubmit,
  isSubmitting = false,
  editingMessage = null,
  onCancelEdit,
}) => {
  const isEditing = editingMessage != null;
  const [audienceType, setAudienceType] = React.useState<AudienceType>('all');
  const [selectedRoles, setSelectedRoles] = React.useState<string[]>([]);
  const [selectedBurnCenterIds, setSelectedBurnCenterIds] = React.useState<number[]>([]);
  const [selectedAccessLevels, setSelectedAccessLevels] = React.useState<string[]>([]);
  const [burnCenterSearch, setBurnCenterSearch] = React.useState('');

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting: isFormSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      title: '',
      body: '',
    },
  });

  React.useEffect(() => {
    if (editingMessage) {
      reset({
        title: editingMessage.title,
        body: editingMessage.body,
      });
      setAudienceType(editingMessage.audienceType);
      setSelectedRoles(editingMessage.roles);
      setSelectedBurnCenterIds(editingMessage.burnCenterIds);
      setSelectedAccessLevels(editingMessage.accessLevels);
      setBurnCenterSearch('');
      return;
    }

    reset({ title: '', body: '' });
    setAudienceType('all');
    setSelectedRoles([]);
    setSelectedBurnCenterIds([]);
    setSelectedAccessLevels([]);
    setBurnCenterSearch('');
  }, [editingMessage, reset]);

  const {
    doRequest: getBurnCenters,
    data: burnCentersResponse,
    isLoading: burnCentersLoading,
  } = useRequest(api.getBurnCenters);

  React.useEffect(() => {
    getBurnCenters({ PageSize: MAX_PAGE_SIZE });
  }, [getBurnCenters]);

  const burnCenters: BurnCenterType[] = React.useMemo(
    () => (burnCentersResponse as { items?: BurnCenterType[] })?.items
      ?? (burnCentersResponse as { data?: BurnCenterType[] })?.data
      ?? [],
    [burnCentersResponse],
  );

  const filteredBurnCenters = React.useMemo(() => {
    const query = burnCenterSearch.trim().toLowerCase();
    if (!query) return burnCenters;
    return burnCenters.filter((center) => formatBurnCenterLabel(center).toLowerCase().includes(query));
  }, [burnCenters, burnCenterSearch]);

  const toggleRole = (role: string) => {
    setSelectedRoles((prev) => (
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    ));
  };

  const toggleBurnCenter = (id: number) => {
    setSelectedBurnCenterIds((prev) => (
      prev.includes(id) ? prev.filter((bcId) => bcId !== id) : [...prev, id]
    ));
  };

  const toggleAccessLevel = (level: string) => {
    setSelectedAccessLevels((prev) => (
      prev.includes(level) ? prev.filter((l) => l !== level) : [...prev, level]
    ));
  };

  const handleFormSubmit = async (values: FormValues) => {
    const isSpecific = audienceType === 'specific';

    await onSubmit({
      title: values.title.trim(),
      body: values.body,
      roles: isSpecific ? selectedRoles : [],
      burnCenterIds: isSpecific ? selectedBurnCenterIds : [],
      accessLevels: isSpecific ? selectedAccessLevels : [],
    });

    if (!isEditing) {
      reset();
      setAudienceType('all');
      setSelectedRoles([]);
      setSelectedBurnCenterIds([]);
      setSelectedAccessLevels([]);
      setBurnCenterSearch('');
    }
  };

  return (
    <Card className="border: 1px solid #e0e0e0;">
      <Card.Body className="p-4">
        {isEditing && (
          <h3 className="h5 fw-semibold mb-3">Edit message</h3>
        )}
        <Form onSubmit={handleSubmit(handleFormSubmit)}>
          <Form.Group className="mb-4" controlId="messageTitle">
            <Form.Label className="fw-semibold">Title (shown in header)</Form.Label>
            <Form.Control
              {...register('title', { required: true })}
              placeholder="e.g. Scheduled maintenance this weekend"
            />
          </Form.Group>

          <Form.Group className="mb-4" controlId="messageBody">
            <Form.Label className="fw-semibold">Detailed content</Form.Label>
            <Controller
              control={control}
              name="body"
              rules={{ required: true }}
              render={({ field: { onChange, value } }) => (
                <QuillRichTextInput
                  value={value}
                  onChange={onChange}
                  placeholder="Write the full message details users will see on the Updates page..."
                />
              )}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Audience</Form.Label>
            <div>
              <Form.Check
                type="radio"
                id="audience-all"
                name="audience"
                label="All users"
                checked={audienceType === 'all'}
                onChange={() => setAudienceType('all')}
              />
              <Form.Check
                type="radio"
                id="audience-specific"
                name="audience"
                label="Specific roles and/or burn centers"
                checked={audienceType === 'specific'}
                onChange={() => setAudienceType('specific')}
              />
            </div>
          </Form.Group>

          {audienceType === 'specific' && (
            <div style={{
              border: '1px solid #e0e0e0', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1rem',
            }}
            >
              <Form.Group className="mb-0">
                <Form.Label className="text-muted mb-2">User roles</Form.Label>

                <Row className="g-2">
                  {TARGET_ROLES.map((role) => (
                    <Col xs={12} md={6} key={role}>
                      <Form.Check
                        type="checkbox"
                        id={`role-${role}`}
                        label={RolesLabels[role]}
                        checked={selectedRoles.includes(role)}
                        onChange={() => toggleRole(role)}
                      />
                    </Col>
                  ))}
                </Row>
              </Form.Group>

              <Form.Group className="mb-0">
                <Form.Label className="text-muted mt-3 mb-2">Burn center access levels</Form.Label>

                <Row className="g-2">
                  {ACCESS_LEVELS.map((level) => (
                    <Col xs={12} md={6} key={level}>
                      <Form.Check
                        type="checkbox"
                        id={`access-level-${level}`}
                        label={level}
                        checked={selectedAccessLevels.includes(level)}
                        onChange={() => toggleAccessLevel(level)}
                      />
                    </Col>
                  ))}
                </Row>
              </Form.Group>

              <Form.Group className="mb-1">
                <Form.Label className="text-muted mt-3 mb-2">Burn centers</Form.Label>
                <InputGroup className="mb-2">
                  <InputGroup.Text>
                    <FontAwesomeIcon icon={faMagnifyingGlass} />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Search burn centers..."
                    value={burnCenterSearch}
                    onChange={(e) => setBurnCenterSearch(e.target.value)}
                  />
                </InputGroup>
                <div
                  className="border rounded p-2"
                  style={{ maxHeight: '12rem', overflowY: 'auto' }}
                >
                  {burnCentersLoading && <div className="text-muted small p-2">Loading burn centers...</div>}
                  {!burnCentersLoading && filteredBurnCenters.length === 0 && (
                    <div className="text-muted small p-2">No burn centers found.</div>
                  )}
                  {filteredBurnCenters.map((center) => (
                    <Form.Check
                      key={center.id}
                      type="checkbox"
                      id={`burn-center-${center.id}`}
                      className="mb-1"
                      label={formatBurnCenterLabel(center)}
                      checked={selectedBurnCenterIds.includes(center.id)}
                      onChange={() => toggleBurnCenter(center.id)}
                    />
                  ))}
                </div>
              </Form.Group>
            </div>
          )}

          <div className="d-flex gap-2">
            <SVButton type="submit" variant="secondary" disabled={isFormSubmitting || isSubmitting}>
              <BsSend className="me-2" />
              {isEditing ? 'Save changes' : 'Post message'}
            </SVButton>
            {isEditing && onCancelEdit && (
              <SVButton type="button" variant="outline-secondary" onClick={onCancelEdit}>
                Cancel
              </SVButton>
            )}
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default PostMessageForm;
